<script lang="tsx" setup>
import { ref, computed, onMounted, onUnmounted } from "vue"
import type { KirbyBlock } from "@/queries/blocks"
import { cleanHTML } from "@/utils/clean"
import {
  buildSpringEasing,
  SPRING_DURATION_MS,
} from "../../composables/use-swiper-spring"
import { useMenuGeometry } from "@/composables/use-menu-geometry"
import Slideshow from "../Slideshow/Slideshow.vue"

// Same eased curve / duration the slides travel on, so the caption feels
// physically attached to the media as it glides to hug each new slide.
const springEase = buildSpringEasing(SPRING_DURATION_MS)

withDefaults(
  defineProps<{
    blocks: KirbyBlock[]
    initialSlide?: number
  }>(),
  { initialSlide: 0 },
)

defineEmits<{
  slideChange: [index: number]
  loaded: []
}>()

// Gap between the media's bottom edge and the caption, and below the caption
// when the region grows to fit it. Matches the slideshow strip's py-3 (12px).
const VERTICAL_PADDING = 12

// Mobile only: vertical clearance so the fixed menu pill never overlaps the
// media. The pill sits at `top-3` (12px) and is `collapsedHeight` tall; we want
// an `mt-3` (12px) gap below it before content starts. Mirrored as the bottom
// padding so the media + caption pair reads as optically balanced.
const menuGeom = useMenuGeometry()
const MENU_TOP = 12 // MenuContainer's `top-3`
const MENU_GAP = 12 // desired `mt-3` between the menu and the content
const mobilePad = computed(
  () => MENU_TOP + menuGeom.collapsedHeight("closed") + MENU_GAP,
)

// Desktop (lg) keeps the original centred-box layout; mobile shrink-wraps the
// region to the media — see `regionHeight` / `boxTransform`.
const isDesktop = ref(false)

// Live measurements.
const boxRef = ref<HTMLDivElement | null>(null)
const captionRef = ref<HTMLDivElement | null>(null)
const boxHeight = ref(0)
const captionHeight = ref(0)
// The box sits below the region wrapper's top padding (mobilePad on mobile, 0
// on lg). Both the caption offset and the region height must include it.
const boxTop = ref(0)

// Active slide's rendered media height (px). `null` until the first slide reports
// — caption falls back to the box bottom in the meantime.
const contentHeight = ref<number | null>(null)
const activeCaption = ref("")

const onHeightChange = (height: number) => {
  contentHeight.value = height
}

const captionTop = computed(() => {
  const ch = contentHeight.value ?? boxHeight.value
  if (isDesktop.value) {
    // Media is centred in the fixed box (slide-strip's py-3 is symmetric, so
    // centre = boxHeight/2); the caption hangs from the media's bottom edge.
    return boxTop.value + boxHeight.value / 2 + ch / 2 + VERTICAL_PADDING
  }
  // Mobile: the media is pulled to the top of the box (see `boxTransform`), so
  // the caption hangs directly below the media's rendered height. boxTop equals
  // mobilePad (region has no border), but use mobilePad directly to avoid a
  // frame of ResizeObserver lag.
  return mobilePad.value + ch + VERTICAL_PADDING
})

// Mobile: pull the media to the top of the box so the centring whitespace
// (boxHeight − contentHeight)/2 above it is removed; the region then clips the
// box's now-empty lower half. Desktop keeps the media centred (no transform).
const boxTransform = computed(() => {
  if (isDesktop.value) return undefined
  const ch = contentHeight.value ?? boxHeight.value
  return `translateY(${-(boxHeight.value - ch) / 2}px)`
})

const regionHeight = computed(() => {
  if (boxHeight.value === 0) return null

  if (isDesktop.value) {
    // Desktop unchanged: only grow when a caption overflows the centred box,
    // leaving a 12px bottom gap; otherwise let the box's height drive the region.
    if (!activeCaption.value) return null
    const base = boxHeight.value + boxTop.value * 2
    const needed = captionTop.value + captionHeight.value + VERTICAL_PADDING
    return Math.max(base, needed)
  }

  // Mobile: shrink-wrap to the media (+ caption) so the slideshow is as short as
  // possible. mobilePad is the menu-clearance top padding; mirroring it as the
  // bottom padding keeps the media + caption pair optically balanced.
  const ch = contentHeight.value ?? boxHeight.value
  const caption = activeCaption.value
    ? VERTICAL_PADDING + captionHeight.value
    : 0
  return mobilePad.value + 30 + ch + caption
})

// Region padding (mobile only) and explicit height. Border-box means the height
// includes the padding, and the 50vh box overflows it (clipped) — the box stays
// full height as a stable fit area so slides never squish on change.
const regionStyle = computed(() => {
  const height =
    regionHeight.value != null ? `${regionHeight.value}px` : undefined
  if (isDesktop.value) return height != null ? { height } : undefined
  return {
    paddingTop: `${mobilePad.value}px`,
    paddingBottom: `${mobilePad.value}px`,
    height,
  }
})

let boxRo: ResizeObserver | null = null
let captionRo: ResizeObserver | null = null
let lgQuery: MediaQueryList | null = null
let onLgChange: (() => void) | null = null

onMounted(() => {
  boxRo = new ResizeObserver(entries => {
    boxHeight.value = entries[0].borderBoxSize[0].blockSize
    boxTop.value = boxRef.value?.offsetTop ?? 0
  })
  if (boxRef.value) boxRo.observe(boxRef.value)

  captionRo = new ResizeObserver(entries => {
    captionHeight.value = entries[0].borderBoxSize[0].blockSize
  })
  if (captionRef.value) captionRo.observe(captionRef.value)

  // Tailwind's `lg` breakpoint — drives the region-balancing branch above.
  lgQuery = window.matchMedia("(min-width: 1024px)")
  onLgChange = () => (isDesktop.value = lgQuery!.matches)
  onLgChange()
  lgQuery.addEventListener("change", onLgChange)
})

onUnmounted(() => {
  boxRo?.disconnect()
  captionRo?.disconnect()
  if (lgQuery && onLgChange) lgQuery.removeEventListener("change", onLgChange)
})

const slideshowRef = ref<InstanceType<typeof Slideshow> | null>(null)

defineExpose({
  slideNext: () => slideshowRef.value?.slideNext(),
  slidePrev: () => slideshowRef.value?.slidePrev(),
})
</script>

<template>
  <div class="-my-px relative">
    <!-- Region. Desktop: grows downward to fit a caption that overflows the
         centred box. Mobile: shrink-wraps the media + caption with symmetric
         menu-clearance padding (regionStyle); the 50vh box overflows and is
         clipped, so it stays a stable fit area. -->
    <div class="relative overflow-hidden" :style="regionStyle">
      <!-- Slideshow box. Desktop centres the media; mobile pulls it to the top
           (boxTransform) so there's no wasted air above it. -->
      <div
        ref="boxRef"
        class="lg:h-[66vh] h-[50vh] relative w-full media-anchor"
        :style="boxTransform != null ? { transform: boxTransform } : undefined"
      >
        <Slideshow
          ref="slideshowRef"
          :blocks="blocks"
          :initial-slide="initialSlide"
          @caption-change="activeCaption = $event"
          @slide-change="$emit('slideChange', $event)"
          @height-change="onHeightChange"
          @loaded="$emit('loaded')"
        />
      </div>

      <!-- Caption: absolutely positioned to hug the active media's bottom edge.
           pointer-events-none on the wrapper so empty space passes clicks
           through to the arrows; the text block re-enables events for links. -->
      <div
        ref="captionRef"
        class="absolute left-0 top-0 w-full px-4 z-60 pointer-events-none caption-anchor"
        :style="{ transform: `translateY(${captionTop}px)` }"
      >
        <div
          v-if="activeCaption"
          :key="activeCaption"
          class="mx-auto text-center small text-sm lg:max-w-2/4 body-text text-black py-2 px-4 pointer-events-auto"
          v-html="cleanHTML(activeCaption)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Caption glides (transform) on the same spring curve the slides travel on, so
   it never jumps when the slide changes. The region's own height changes in one
   step on purpose — StudentGalleryWrapper's ResizeObserver + useMotion smooths
   the outer height; animating it here too would make the two transitions chase
   each other (a lerp tracking a lerp), which drags. */
.caption-anchor {
  transition: transform v-bind("SPRING_DURATION_MS + 'ms'") v-bind(springEase);
  will-change: transform;
}

/* Mobile: the media's top-align transform glides on the same spring as the
   caption, so the strip re-centres smoothly as slides of differing heights
   change. Desktop never sets a transform, so this is inert there. */
.media-anchor {
  transition: transform v-bind("SPRING_DURATION_MS + 'ms'") v-bind(springEase);
  will-change: transform;
}

.caption-fade-enter-active,
.caption-fade-leave-active {
  transition: opacity 220ms ease;
}

.caption-fade-enter-from,
.caption-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .caption-anchor,
  .media-anchor {
    transition: none;
  }
}
</style>
